import { useAppSelector } from '@/stores/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PlayerListItem from './list/PlayerListItem';
import styled from 'styled-components';
import { getPlayerList } from '@/api/player-list/getPlayerList';
import { player } from '@/stores/slices/player-list/playerSlice';
import { KeywordItemType } from '@/api/type/commonTypes';
import { useRouterQuery } from '@/hook/router-query/useRouterQuery';
import { useLoading } from '@/context/LoadingContenxt';
import CommonTitle from '@/components/title/CommonTitle';
import CommonSearch from '@/components/search/CommonSearch';
import Pagination from '@/components/pagination/Pagination';
import { useTranslation } from 'react-i18next';

const SIZE = 30;
const PlayerListPage = () => {
  const { t } = useTranslation();
  const keywordTypeData: KeywordItemType[] = useMemo(
    () => [
      {
        keyword: 'LOGIN_ID',
        i18nKey: 'common.loginId',
      },
      {
        keyword: 'NAME',
        i18nKey: 'common.subscriberName',
      },
      {
        keyword: 'CLINIC_NAME',
        i18nKey: 'playerList.officeName',
      },
      {
        keyword: 'REGISTERED_AT',
        i18nKey: 'common.registeredDate',
      },
    ],
    []
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? 1);
  const size = Number(searchParams.get('size') ?? SIZE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordItemType>(keywordTypeData[0]);
  const dispatch = useDispatch();
  const keywordType = searchParams.get('keywordType');
  const { playerList } = useAppSelector((state) => state.player);
  const { startLoading, stopLoading } = useLoading();
  const { updateQuery } = useRouterQuery();
  const searchValueRef = useRef('');
  const navigate = useNavigate();

  /** 목록 fetch 함수 */
  const fetchPlayerList = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlayerList({
        keywordType: keywordType ?? 'LOGIN_ID',
        keyword: searchValueRef.current,
        page: currentPage,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(player.setPlayerList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, keywordType, startLoading, stopLoading]);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setSearchParams({
      page: String(page),
      size: String(size),
    });
  };

  /** 검색 value 변경 */
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      searchValueRef.current = e.target.value;
    },
    []
  );

  /** 검색 아이콘 클릭 및 엔터 */
  const onClickSearch = useCallback(async () => {
    startLoading();
    try {
      const res = await getPlayerList({
        keywordType: selectedKeywordType.keyword,
        keyword: searchValueRef.current,
        page: 1,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(player.setPlayerList(res.data));
        updateQuery({
          params: {
            keywordType: selectedKeywordType.keyword,
            keyword: searchValueRef.current,
            page: 1,
            size: SIZE,
          },
          route: '/player-list',
        });
      } else {
        if (res.error.code === 'E_DATE_001') {
          alert(t('common.searchPlaceholder'));
        }
      }
    } finally {
      stopLoading();
    }
  }, [
    dispatch,
    selectedKeywordType.keyword,
    startLoading,
    stopLoading,
    t,
    updateQuery,
  ]);

  /** 검색 초기화 */
  const onClickSearchReset = useCallback(() => {
    setSearchParams({
      page: String(1),
      size: String(size),
    });
    setSearchValue('');
    searchValueRef.current = '';
    fetchPlayerList();
    navigate('/player-list');
  }, [fetchPlayerList, navigate, setSearchParams, size]);

  /** keywordType 저장 */
  const onDispatchKeyword = useCallback((keywordType: KeywordItemType) => {
    setSelectedKeywordType(keywordType);
  }, []);

  useEffect(() => {
    fetchPlayerList();
  }, [fetchPlayerList]);

  if (!playerList) return;

  return (
    <Container>
      <CommonTitle
        title={t('playerList.title')}
        count={playerList.totalElements}
      />
      <SearchWrapper>
        <CommonSearch
          value={searchValue}
          onChange={onChangeSearch}
          onClickSearch={onClickSearch}
          onClickReset={onClickSearchReset}
          options={keywordTypeData}
          onSelect={onDispatchKeyword}
          selectedOption={selectedKeywordType}
        />
      </SearchWrapper>
      <ListWrapper>
        <PlayerListItem items={playerList.items} />
      </ListWrapper>
      <Pagination
        totalItems={playerList.totalElements}
        itemsPerPage={SIZE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default PlayerListPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SearchWrapper = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ListWrapper = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;
